import { Link } from "react-router-dom";
import { VENTURES } from "@/lib/data";
import { Reveal, Overline } from "@/components/Reveal";
import { useLang } from "@/i18n";
import { ArrowUpRight } from "lucide-react";

/** Home-page overview of the three verticals; each card links to its own page. */
const VenturesOverview = () => {
  const { t, data } = useLang();

  return (
    <section id="ventures" data-testid="ventures-section" className="py-24 md:py-32 bg-[#fdfbf5]">
      <div className="w-full max-w-[1440px] mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
          <div>
            <Reveal><Overline>{t("ventures.overline")}</Overline></Reveal>
            <Reveal delay={0.05}>
              <h2 className="mt-4 font-display font-bold tracking-tight leading-tight text-4xl md:text-5xl lg:text-6xl max-w-[14ch]">
                {t("ventures.heading")}
              </h2>
            </Reveal>
          </div>
          <Reveal delay={0.1}>
            <p className="text-[#5f6b57] max-w-sm text-base md:text-lg">{t("ventures.para")}</p>
          </Reveal>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
          {VENTURES.map((v, i) => {
            const dv = data.ventures?.[v.slug] || {};
            const title = dv.title || v.title;
            return (
              <Reveal key={v.slug} delay={i * 0.08} className="h-full">
                <Link
                  to={`/${v.slug}`}
                  data-testid={`venture-card-${v.slug}`}
                  className="group relative h-full bg-[#ffffff] rounded-2xl border border-[#1d3a1f]/10 card-soft hover:border-[#d3a53a] hover:-translate-y-1.5 hover:shadow-xl transition-all duration-300 overflow-hidden flex flex-col"
                >
                  <div className="relative h-56 overflow-hidden">
                    <img src={v.image} alt={v.imageAlt} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0d1f12]/75 via-[#0d1f12]/10 to-transparent" />
                    <span className="absolute top-4 start-4 text-xs font-bold tracking-[0.15em] uppercase px-3 py-1.5 rounded-full bg-[#0d1f12]/70 border border-white/20 text-[#e7c46b]">{dv.status || v.status}</span>
                    <span className="absolute bottom-3 end-4 font-display font-extrabold text-5xl text-white/25">{v.no}</span>
                  </div>
                  <div className="p-7 flex flex-col flex-1">
                    <div className="flex items-start justify-between gap-4">
                      <h3 className="font-display font-bold text-2xl md:text-3xl tracking-tight">{title}</h3>
                      <ArrowUpRight className="text-[#5f6b57] group-hover:text-[#d3a53a] group-hover:-translate-y-1 group-hover:translate-x-1 transition-all duration-300 shrink-0" />
                    </div>
                    <p className="mt-4 text-[#5f6b57] leading-relaxed flex-1">{dv.tagline || v.tagline}</p>
                    <span className="mt-6 text-sm font-bold text-[#d3a53a]">{t("ventures.explore")} {title} →</span>
                  </div>
                </Link>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default VenturesOverview;
